import { useEffect, useRef, useState } from "react";
import usePlayerUtils from "../../hooks/usePlayerUtils";
import CONFIG from "../../config";

import "./GuideMain.css"

export default function GuideMain() {
    const MIN_DRAG_START = 15;
    const [pageIndex, setPageIndex] = useState(0);
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [weekPages, setWeekPages] = useState([]);

    const guideRef = useRef(null);
    const touchStartX = useRef(0);

    const {
        currentTrack,
      } = usePlayerUtils();

    useEffect(() => {
        const fetchWeekGuide = async () => {
            try {
                const res = await fetch(`${CONFIG.apiBase}/tasks/week-guide`);
                if (!res.ok) throw new Error("Failed to fetch week guide");

                const data = await res.json();
                setPages(data);
            } catch (err) {
                console.error("Error fetching week guide:", err);
            }
        };

        fetchWeekGuide();
    }, [currentTrack?.title]);

    const setPages = (data) => {
        const today = new Date();
        const year = today.getUTCFullYear();
        const month = today.getUTCMonth();
        const date = today.getUTCDate();

        const timeFormattedTasks = data.map((d) => {
            const formattedTasks = d.tasks.map((t) => {
                const [hours, minutes] = t.time.split(":").map(Number);

                const utcDate = new Date(Date.UTC(year, month, date, hours, minutes));

                const localTime = utcDate.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });

                return {
                    id: t.id,
                    time: localTime,
                    name: t.name,
                    desc: t.desc,
                };
            });

            return { ...d, tasks: formattedTasks };
        });

        setWeekPages(timeFormattedTasks);
    };

    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        setIsDragging(false);
    };

    const handleTouchMove = (e) => {
        const diff = e.touches[0].clientX - touchStartX.current;

        if (!isDragging && Math.abs(diff) > MIN_DRAG_START) {
            setIsDragging(true);
        }

        if (!isDragging) return;

        if ((pageIndex === 0 && diff > 0) || (pageIndex === weekPages.length - 1 && diff < 0)) {
            setDragX(diff * 0.3);
        } else {
            setDragX(diff);
        }
    };

    const handleTouchEnd = () => {
        const containerWidth = guideRef.current?.offsetWidth ?? window.innerWidth;
        const dragThreshold = containerWidth / 4;

        setIsDragging(false);

        if (dragX < -dragThreshold) {
            if (pageIndex === weekPages.length - 1) {
                setDragX(0);
            } else {
                setPageIndex(i => i + 1);
                setDragX(0);
            }
        } else if (dragX > dragThreshold / 4 && pageIndex === 0) {
            setDragX(0);
        } else if (dragX > dragThreshold) {
            if (pageIndex !== 0) {
                setPageIndex(i => i - 1);
                setDragX(0);
            }
        } else {
            setDragX(0);
        }
    };

    const getTaskStatus = (task, nextTask, taskDate) => {
        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const taskDay = new Date(taskDate);
        taskDay.setHours(0, 0, 0, 0);

        if (taskDay.getTime() !== today.getTime()) return "upcoming";

        const [hours, minutes] = task.time.split(":").map(Number);
        const taskTime = new Date();
        taskTime.setHours(hours, minutes, 0, 0);

        let nextTaskTime;
        if (nextTask) {
            const [nextHours, nextMinutes] = nextTask.time.split(":").map(Number);
            nextTaskTime = new Date();
            nextTaskTime.setHours(nextHours, nextMinutes, 0, 0);
        }

        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        if (nextTaskTime) {
            if (now >= taskTime && now < nextTaskTime) return "active";
            if (now >= nextTaskTime) return "inactive";
        } else {
            if (now >= taskTime && now <= endOfDay) return "active";
            if (now > endOfDay) return "inactive";
        }

        return "upcoming";
    };

    const guide = () => {
        return (
            <div className="d-expanded-guide">
                <div className="d-expanded-guide-content">
                    {guideHeader()}
                    {guideList()}
                </div>
            </div>
        );
    };

    const guideHeader = () => {
        return (
            <div className="d-expanded-guide-title">
                <div className="d-text">NEXT WEEK GUIDE</div>
            </div>
        );
    }

    const guideList = () => {
        const colorLeftArrow = pageIndex === 0 ? "#666666ff" : "#F5F5F3";
        const colorRightArrow = pageIndex === weekPages.length - 1 ? "#666666ff" : "#F5F5F3";

        return (
            <div className="d-guide-list-main">
                <div className="d-date-arrows">
                    <div className="d-date-arrows-content">
                        <div className="d-left-arrow">
                            <button
                                style={{ color: colorLeftArrow }}
                                onClick={() => { setPageIndex((i) => Math.max(0, i - 1)) }}
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>
                        </div>
                        <div className="d-day">{weekPages[pageIndex]?.label}</div>
                        <div className="d-right-arrow">
                            <button
                                style={{ color: colorRightArrow }}
                                onClick={() => {
                                    if (pageIndex === weekPages.length - 1) {
                                    } else {
                                        setPageIndex((i) => Math.min(weekPages.length - 1, i + 1));
                                    }
                                }}
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="d-expanded-guide-list-main">
                <div
                    className="d-expanded-guide-list"
                    style={{
                        display: "flex",
                        width: `${weekPages.length * 100}%`,
                        transform: `translateX(calc(${-pageIndex * (100 / weekPages.length)}% + ${dragX}px))`,
                        transition: isDragging ? "none" : "transform 0.3s ease",
                    }}
                >
                    {weekPages.map((day, idx) => (
                        <div key={`${day.key}-${idx}`} className="d-page" style={{ flex: `0 0 calc(100% / ${weekPages.length})` }}>
                            <ul>
                                {day.tasks?.map((task, idx, arr) => {
                                    const nextTask = arr[idx + 1];
                                    const status = getTaskStatus(task, nextTask, weekPages[pageIndex]?.date);

                                    return (
                                        <li key={`${pageIndex}-${task.id}-${idx}`} className={`d-task-item ${status}`} title={task.name}>
                                            <div className="d-start-end-time">{task.time}</div>
                                            <div className="d-task-content">
                                                <div className="d-task-name">{task.name}</div>
                                                <div className="d-task-description">{task.desc}</div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
            </div>
            
        );
    };

    return (
        <div
            ref={guideRef}
            className="d-guide-main-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {guide()}
        </div>
    );
};